/** Catmull-Rom smoothed spline with left→right sweep-in, travelling dot, and gradient fill.
 *  Mirrors the Android SplineChart animation. */
import { memo, useEffect, useId, useMemo, useRef, useState } from "react";

interface Props {
  values: number[];
  color?: string;
  height?: number;
  width?: number;
}

const SWEEP_MS = 1400;

export const SplineChart = memo(function SplineChart({ values, color = "var(--amber)", height = 80, width = 600 }: Props) {
  const gid = useId().replace(/:/g, "");
  const pathRef = useRef<SVGPathElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const [pathLen, setPathLen] = useState(0);
  const [dotPos, setDotPos] = useState<{ x: number; y: number } | null>(null);
  const [dotR, setDotR] = useState(0);
  const [lineOffset, setLineOffset] = useState(0);
  const [fillOpacity, setFillOpacity] = useState(0);

  const { line, fill, tip } = useMemo(() => buildPaths(values, width, height), [values, width, height]);

  useEffect(() => {
    const el = pathRef.current;
    if (!el) return;
    const total = el.getTotalLength();
    setPathLen(total);
    setLineOffset(total);
    setDotPos(null);
    setDotR(0);
    setFillOpacity(0);

    let raf: number;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min((now - start) / SWEEP_MS, 1);
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      setLineOffset(total * (1 - ease));

      const pos = el.getPointAtLength(total * ease);
      setDotPos({ x: pos.x, y: pos.y });

      if (t >= 1) {
        setDotR(3.5);
        setFillOpacity(1);
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [line]);

  if (values.length < 2) {
    return <svg className="spline" viewBox={`-2 0 ${width + 4} ${height}`} preserveAspectRatio="none" />;
  }

  return (
    <svg className="spline" viewBox={`-2 0 ${width + 4} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`g${gid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.35" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {[0, 0.5, 1].map((f) => (
        <line key={f} x1="0" x2={width} y1={f * (height - 16) + 8} y2={f * (height - 16) + 8} stroke="var(--edge)" strokeWidth="0.5" opacity="0.4" />
      ))}

      <path d={fill} fill={`url(#g${gid})`} opacity={fillOpacity} style={{ transition: "opacity 0.4s ease" }} />

      <path
        ref={pathRef}
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={pathLen || undefined}
        strokeDashoffset={lineOffset}
      />

      {dotPos && (
        <circle
          cx={dotPos.x}
          cy={dotPos.y}
          r={dotR}
          fill={color}
          style={{ transition: "r 0.4s cubic-bezier(0.34,1.56,0.64,1)" }}
        />
      )}
    </svg>
  );
});

function buildPaths(values: number[], w: number, h: number) {
  if (values.length < 2) return { line: "", fill: "", tip: null as { x: number; y: number } | null };
  const pad = 8;
  const usable = h - pad * 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const pts = values.map((v, i) => ({
    x: (w * i) / (values.length - 1),
    y: pad + usable - (range > 0 ? ((v - min) / range) * usable : 0.5 * usable),
  }));
  let d = `M ${pts[0]!.x} ${pts[0]!.y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = i === 0 ? pts[i]! : pts[i - 1]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = i + 2 < pts.length ? pts[i + 2]! : p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
  }
  const last = pts[pts.length - 1]!;
  const fill = `${d} L ${last.x} ${h} L ${pts[0]!.x} ${h} Z`;
  return { line: d, fill, tip: last };
}
